import {BehaviorSubject, debounceTime, map, Observable} from "rxjs";
import {DashboardFilter, DashboardFilterInterface} from "./dashboard_filter";
import {DashboardFilterUISettings, DashboardFilterUISettingsInterface} from "./dashboard_filter_ui_settings";

export interface RangeFilterSettingsInterface {
  min: number,
  max: number,
}


export class RangeFilterSettings {
  min: number;
  max: number;

  constructor(args: RangeFilterSettingsInterface) {
    this.min = args.min;
    this.max = args.max;
  }
}


export interface RangeFilterUISettingsInterface extends DashboardFilterUISettingsInterface {
  enableInput?: boolean;
}


export class RangeFilterUISettings extends DashboardFilterUISettings {
  enableInput?: boolean;

  constructor(args: RangeFilterUISettingsInterface) {
    super(args);
    this.enableInput = args.enableInput ?? true;
  }
}


export interface RangeFilterInterface<V, PFV> extends DashboardFilterInterface<V, RangeFilterUISettings, number, PFV> {
  settings: RangeFilterSettings;
  uiSettings: RangeFilterUISettings;
}

export class DashboardRangeFilter<V, PFV = any> extends DashboardFilter<V, RangeFilterUISettings, number, PFV> {
  readonly settings: RangeFilterSettings;
  private _rangeValueSubject: BehaviorSubject<V>;
  private _rangeValueObservable$: Observable<V>;

  constructor(args: RangeFilterInterface<V, PFV>) {
    super(args);
    this.settings = args.settings;
    this._rangeValueSubject = new BehaviorSubject<V>(args.initialValue);
    this._rangeValueObservable$ = this._getRangeValueSubjectObservable()
  }

  override initialize() {
    if (this.parentFilterSettings && !this.parentFilter || !this.parentFilterSettings && this.parentFilter) {
      throw Error(`${this.name}: Parent filter and its settings must be provided before initializing options`);
    }
  }

  isValueValid(value: any): boolean {
    return true;
  }

  override get value(): V {
    return this._rangeValueSubject.value;
  }

  override set value(value: V) {
    this._rangeValueSubject.next(value);
  }

  override setValueNoChecks(value: V) {
    this._rangeValueSubject.next(value);
  }

  override get value$(): Observable<V> {
    return this._rangeValueObservable$
  }

  _getRangeValueSubjectObservable(): Observable<V> {
    return this._rangeValueSubject.pipe(
      debounceTime(250),
      map((v) => v)
    );
  }
}

export class DashboardSliderFilter<V, PFV = any> extends DashboardFilter<V, RangeFilterUISettings, number, PFV> {
  readonly settings: RangeFilterSettings;
  private _rangeValueSubject: BehaviorSubject<V>;
  private _rangeValueObservable$: Observable<V>;

  constructor(args: RangeFilterInterface<V, PFV>) {
    super(args);
    this.settings = args.settings;
    this._rangeValueSubject = new BehaviorSubject<V>(args.initialValue);
    this._rangeValueObservable$ = this._getRangeValueSubjectObservable()
  }

  override initialize() {
    if (this.parentFilterSettings && !this.parentFilter || !this.parentFilterSettings && this.parentFilter) {
      throw Error(`${this.name}: Parent filter and its settings must be provided before initializing options`);
    }
  }

  isValueValid(value: any): boolean {
    return true;
  }

  override get value(): V {
    return this._rangeValueSubject.value;
  }

  override set value(value: V | undefined | null) {
    if (value == null) {
      value = this._parseNullValue(value);
    }
    if (!this.isValueValid(value)) {
      throw Error(`Value ${value} is not valid for filter ${this.name}`);
    }
    if (value === this.value) {
      return;
    }
    this._rangeValueSubject.next(value);
  }

  override get value$(): Observable<V> {
    return this._rangeValueObservable$
  }

  _getRangeValueSubjectObservable(): Observable<V> {
    return this._rangeValueSubject.pipe(
      debounceTime(250),
      map((v) => v)
    );
  }
}
